/**
 * MIØ Warenkorb-Leiste am Telefon (sections/mio-warenkorb-leiste.liquid).
 *
 * Nach jeder Änderung am Warenkorb wird der Abschnitt über die Section
 * Rendering API neu geholt. So stehen Beträge, Gratisversand-Hinweis und
 * Texte genau so da wie im seitlichen Warenkorb, ohne Preisformat im Code.
 * Ein Tipp auf den linken Teil öffnet den seitlichen Warenkorb; ohne ihn
 * führt der Link zur Warenkorbseite.
 */
import { StandardEvents } from '@shopify/events';

class MioWarenkorbLeiste extends HTMLElement {
  connectedCallback() {
    this.abschnitt = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') ?? '';
    this.addEventListener('click', this.klick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.nachAenderung);
    window.addEventListener('pageshow', this.zurueck);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.klick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.nachAenderung);
    window.removeEventListener('pageshow', this.zurueck);
    this.abbruch?.abort();
  }

  /** @param {MouseEvent} e */
  klick = (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('[data-oeffnen]')) return;
    const schublade = document.getElementById('cart-drawer');
    if (typeof schublade?.open !== 'function') return;
    e.preventDefault();
    schublade.open();
  };

  /** Erst neu laden, wenn Shopify die Änderung bestätigt hat. */
  nachAenderung = (event) => {
    const fertig = event?.promise ?? Promise.resolve();
    fertig.then(
      () => this.neuLaden(),
      (fehler) => {
        if (fehler?.name !== 'AbortError') this.neuLaden();
      }
    );
  };

  /** Aus dem Browser-Verlauf zurück: Warenkorb kann sich woanders geändert haben. */
  zurueck = (event) => {
    if (event.persisted) this.neuLaden();
  };

  async neuLaden() {
    if (!this.abschnitt) return;
    this.abbruch?.abort();
    this.abbruch = new AbortController();
    const wurzel = window.Shopify?.routes?.root ?? '/';
    try {
      const antwort = await fetch(`${wurzel}?sections=${encodeURIComponent(this.abschnitt)}`, {
        signal: this.abbruch.signal,
      });
      if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
      const html = (await antwort.json())[this.abschnitt];
      const neu = new DOMParser().parseFromString(html ?? '', 'text/html').querySelector('mio-warenkorb-leiste');
      const inhaltNeu = neu?.querySelector('[data-inhalt]');
      const inhaltAlt = this.querySelector('[data-inhalt]');
      if (!neu || !inhaltNeu || !inhaltAlt) return;
      inhaltAlt.replaceWith(inhaltNeu);
      // Stil einmal berechnen lassen, sonst springt die Leiste beim ersten
      // Artikel ohne Einfahren an ihren Platz.
      inhaltNeu.getBoundingClientRect();
      const warAn = this.classList.contains('is-an');
      this.classList.toggle('is-an', neu.classList.contains('is-an'));
      // „Nach oben“ rückt mit der Leiste; assets/mio-kopf.js misst dann neu.
      if (warAn !== this.classList.contains('is-an')) document.dispatchEvent(new Event('mio:neu-messen'));
    } catch (fehler) {
      // Die Leiste bleibt dann beim alten Stand; der Warenkorb selbst ist davon unberührt.
      if (fehler?.name !== 'AbortError') console.warn('[mio-warenkorb-leiste]', fehler);
    }
  }
}

if (!customElements.get('mio-warenkorb-leiste')) customElements.define('mio-warenkorb-leiste', MioWarenkorbLeiste);

/**
 * Theme-Vorschau: Shopify legt unten eine eigene Leiste über die Seite
 * (#PBarNextFrameWrapper, nur für Shop-Betreiber sichtbar). Am iPhone deckt
 * sie die Warenkorb-Leiste fast ganz zu. Deren Höhe landet deshalb in
 * --mio-vorschauleiste, und Leiste, Platzhalter und „Nach oben“ rücken
 * darüber. Im veröffentlichten Theme gibt es die Vorschauleiste nicht.
 */
function vorschauleisteBeachten() {
  if (window.Shopify?.theme?.role === 'main' || window.Shopify?.designMode) return;

  const setzen = (leiste) => {
    const hoehe = leiste?.isConnected ? Math.round(leiste.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--mio-vorschauleiste', `${hoehe}px`);
    document.dispatchEvent(new Event('mio:neu-messen'));
  };
  const beobachten = (leiste) => {
    setzen(leiste);
    if ('ResizeObserver' in window) new ResizeObserver(() => setzen(leiste)).observe(leiste);
  };

  const vorhanden = document.getElementById('PBarNextFrameWrapper');
  if (vorhanden) {
    beobachten(vorhanden);
    return;
  }
  // Shopify fügt die Leiste erst nach dem Laden ein.
  const suche = new MutationObserver(() => {
    const leiste = document.getElementById('PBarNextFrameWrapper');
    if (!leiste) return;
    suche.disconnect();
    beobachten(leiste);
  });
  suche.observe(document.body, { childList: true });
}

vorschauleisteBeachten();
